import { useCallback, useEffect, useRef, useState } from 'react';
import { getApiErrorMessage, knetTerminalAPI } from '@matix/api-client';
import type { KnetTerminalTxn } from '@matix/types';
import { fmt } from '../../lib/mock-data';
import { useLang } from '../../lib/language';
import { cardSummary, stepFor } from './knetTerminalFlow';

/**
 * "Waiting for card on the KNET terminal…" (KNET_TERMINAL.md).
 *
 * Queues a PURCHASE (or a REFUND against the original receipt) for this
 * drawer's terminal and polls it once a second. The SCPS agent on the shop PC
 * picks it up, drives eSocket.POS, and reports back:
 *
 *   approved  → onApproved(txn); the PaymentModal adds the slice carrying the
 *               attempt id and the order goes out exactly as today.
 *   declined / error / expired / reversed …
 *             → the reason, and the cashier retries, picks another tender, or
 *               (when the admin allows it) records the tender manually.
 *
 * Cancel while waiting asks the agent to send CANCEL_TRAN; the terminal's
 * answer still decides. If it comes back APPROVED anyway, the cashier chooses
 * to keep it or reverse it — never silently dropped.
 */

const POLL_MS = 1000;

export interface KnetTerminalDialogProps {
  mode: 'purchase' | 'refund';
  amount: number;
  paymentTypeCode: number;
  tenderName: string;
  orderIdempotencyKey: string | null;
  parentReceiptId?: number | null;
  allowManual: boolean;
  onApproved: (txn: KnetTerminalTxn) => void;
  onManual: () => void;
  onClose: () => void;
}

export function KnetTerminalDialog(props: KnetTerminalDialogProps): JSX.Element {
  const { t } = useLang();
  const [txn, setTxn] = useState<KnetTerminalTxn | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const cancelledByCashier = useRef(false);
  const alive = useRef(true);
  const done = useRef(false);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Start an attempt (again on Retry).
  useEffect(() => {
    let stop = false;
    setTxn(null);
    setStartError(null);
    setCancelling(false);
    cancelledByCashier.current = false;
    done.current = false;
    const start =
      props.mode === 'refund'
        ? knetTerminalAPI.refund({
            amount: props.amount,
            payment_type_code: props.paymentTypeCode,
            parent_receipt_id: props.parentReceiptId ?? 0,
            order_idempotency_key: props.orderIdempotencyKey,
          })
        : knetTerminalAPI.purchase({
            amount: props.amount,
            payment_type_code: props.paymentTypeCode,
            order_idempotency_key: props.orderIdempotencyKey,
          });
    start
      .then((x) => {
        if (!stop) setTxn(x);
      })
      .catch((err) => {
        if (!stop) setStartError(getApiErrorMessage(err));
      });
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  // Poll while in flight.
  const txnId = txn?.id ?? null;
  const step = txn ? stepFor(txn) : null;
  useEffect(() => {
    if (txnId == null || step !== 'waiting') return;
    const timer = setInterval(() => {
      knetTerminalAPI
        .get(txnId)
        .then((x) => {
          if (alive.current) setTxn(x);
        })
        .catch(() => undefined);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [txnId, step]);

  // Hand an approval straight back — unless the cashier pressed Cancel, in
  // which case they decide what to do with a card that was charged anyway.
  useEffect(() => {
    if (!txn || done.current) return;
    if (stepFor(txn) === 'approved' && !cancelledByCashier.current) {
      done.current = true;
      props.onApproved(txn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txn]);

  const cancel = useCallback(async () => {
    if (!txn) {
      props.onClose();
      return;
    }
    cancelledByCashier.current = true;
    setCancelling(true);
    try {
      setTxn(await knetTerminalAPI.cancel(txn.id));
    } catch {
      /* the poll carries on and shows the outcome */
    }
  }, [txn, props]);

  const reverse = useCallback(async () => {
    if (!txn) return;
    setBusy(true);
    try {
      setTxn(await knetTerminalAPI.reverse(txn.id));
    } catch (err) {
      setStartError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [txn]);

  const title =
    props.mode === 'refund'
      ? t({ en: 'Refund on the KNET terminal', ar: 'استرداد عبر جهاز كي نت' })
      : t({ en: 'Pay on the KNET terminal', ar: 'الدفع عبر جهاز كي نت' });

  const waiting = !startError && (!txn || step === 'waiting');
  const approvedAfterCancel = txn != null && step === 'approved' && cancelledByCashier.current;
  const failed = startError != null || (txn != null && step === 'failed');
  const reversing = txn?.status === 'reversal_pending';

  return (
    <div className="modal-overlay show">
      <div className="modal" style={{ width: 460, maxWidth: '92vw' }} role="dialog" aria-label={title}>
        <div className="modal-header">
          <div className="modal-header-img" style={{ background: 'var(--accent-light, #dbeafe)', color: 'var(--accent, #2563eb)' }}>
            <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="6" width="18" height="12" rx="2" />
              <path d="M3 10h18M7 15h2" />
            </svg>
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">{title}</div>
            <div className="modal-header-name-ar">
              {props.tenderName} · {fmt(props.amount)}
            </div>
          </div>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center', textAlign: 'center' }}>
          {waiting && (
            <>
              <div className="knet-spinner" aria-hidden="true" style={spinner} />
              <div style={{ fontSize: 16, fontWeight: 600 }}>
                {!txn || txn.status === 'queued'
                  ? t({ en: 'Sending to the terminal…', ar: 'جارٍ الإرسال إلى الجهاز…' })
                  : props.mode === 'refund'
                    ? t({ en: 'Waiting for the card on the KNET terminal…', ar: 'بانتظار البطاقة على جهاز كي نت…' })
                    : t({ en: 'Waiting for card on KNET terminal…', ar: 'بانتظار البطاقة على جهاز كي نت…' })}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {cancelling
                  ? t({ en: 'Cancelling at the terminal…', ar: 'جارٍ الإلغاء على الجهاز…' })
                  : t({ en: 'Ask the customer to tap, insert or swipe and enter the PIN.', ar: 'اطلب من العميل تمرير البطاقة وإدخال الرقم السري.' })}
              </div>
              <button type="button" className="btn-ghost" style={ghostBtn} disabled={cancelling} onClick={() => void cancel()}>
                {t({ en: 'Cancel', ar: 'إلغاء' })}
              </button>
            </>
          )}

          {approvedAfterCancel && txn && (
            <>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--success, #16a34a)' }}>
                {t({ en: 'The card was approved before the cancel reached the terminal.', ar: 'تمت الموافقة على البطاقة قبل وصول الإلغاء إلى الجهاز.' })}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{cardSummary(txn)}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" style={primaryBtn} disabled={busy} onClick={() => props.onApproved(txn)}>
                  {t({ en: 'Keep the payment', ar: 'الاحتفاظ بالدفعة' })}
                </button>
                <button type="button" style={ghostBtn} disabled={busy} onClick={() => void reverse()}>
                  {t({ en: 'Reverse it', ar: 'عكس الدفعة' })}
                </button>
              </div>
            </>
          )}

          {failed && (
            <>
              <div style={{ fontSize: 28, color: reversing ? 'var(--text-muted)' : 'var(--danger, #dc2626)' }}>!</div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                {startError ??
                  txn?.message ??
                  t({ en: 'The terminal did not approve the payment.', ar: 'لم يوافق الجهاز على الدفع.' })}
              </div>
              {txn?.response_code && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {t({ en: 'Response code', ar: 'رمز الرد' })} {txn.response_code}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                <button type="button" style={primaryBtn} onClick={() => setAttempt((n) => n + 1)}>
                  {t({ en: 'Try again', ar: 'إعادة المحاولة' })}
                </button>
                <button type="button" style={ghostBtn} onClick={props.onClose}>
                  {t({ en: 'Choose another method', ar: 'اختيار وسيلة أخرى' })}
                </button>
                {props.allowManual && (
                  <button type="button" style={ghostBtn} onClick={props.onManual}>
                    {t({ en: 'Record manually', ar: 'تسجيل يدوي' })}
                  </button>
                )}
              </div>
            </>
          )}

          {txn && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
              eSocket #{txn.esp_transaction_id} · {txn.status}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const spinner: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: '50%',
  border: '4px solid var(--border, #e5e7eb)',
  borderTopColor: 'var(--accent, #2563eb)',
  animation: 'spin 1s linear infinite',
};
const primaryBtn: React.CSSProperties = {
  padding: '8px 18px',
  background: 'var(--accent, #2563eb)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontWeight: 600,
};
const ghostBtn: React.CSSProperties = {
  padding: '8px 14px',
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 6,
  cursor: 'pointer',
};
